import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSetStickerGroups, type MediaSticker, type MediaStickerPack } from '../api';
import { stickerGroupIds } from '../stickerGroups';
import { stickerMutationError } from '../stickerOrder';
import { sortStickerPacks } from '../stickerSearch';

export function StickerGroupsDialog({
  sticker,
  packs,
  onClose,
}: {
  sticker: MediaSticker | null;
  packs: MediaStickerPack[];
  onClose: () => void;
}) {
  const mutation = useSetStickerGroups();
  const [selected, setSelected] = useState<string[]>([]);
  const [primary, setPrimary] = useState('');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const lastId = useRef<string | null>(null);
  useEffect(() => {
    if (sticker && lastId.current !== sticker.stickerId) {
      setSelected(stickerGroupIds(sticker));
      setPrimary(sticker.packId);
      setQuery('');
      setError(null);
    }
    lastId.current = sticker?.stickerId ?? null;
  }, [sticker]);
  const groups = sortStickerPacks(packs.filter((pack) => pack.status !== 'RETIRED'));
  const selectedGroups = groups.filter((pack) => selected.includes(pack.packId));
  const choices = groups.filter((pack) =>
    `${pack.displayName} ${pack.packId}`.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const valid =
    selected.length > 0 && selected.includes(primary) && selectedGroups.length === selected.length;
  const original = sticker ? stickerGroupIds(sticker) : [];
  const changed =
    primary !== sticker?.packId ||
    selected.length !== original.length ||
    selected.some((id) => !original.includes(id));
  const toggle = (id: string) => {
    const next = selected.includes(id)
      ? selected.filter((value) => value !== id)
      : [...selected, id];
    setSelected(next);
    if (!next.includes(primary)) setPrimary(next[0] ?? '');
  };
  const save = async () => {
    if (!sticker || !valid || !changed || mutation.isPending) return;
    setError(null);
    try {
      await mutation.mutateAsync({
        stickerId: sticker.stickerId,
        groups: { groupIds: selected, primaryGroupId: primary },
      });
      toast.success('Sticker groups saved');
      onClose();
    } catch (reason) {
      setError(stickerMutationError(reason));
    }
  };

  return (
    <Dialog open={!!sticker} onOpenChange={(open) => !open && !mutation.isPending && onClose()}>
      <DialogContent className="max-w-xl" closeDisabled={mutation.isPending}>
        <DialogHeader>
          <DialogTitle>Groups for {sticker?.name}</DialogTitle>
          <DialogDescription>
            Choose one or more groups. Add Trending while keeping the sticker in its original theme.
            Each group has its own sticker order.
          </DialogDescription>
        </DialogHeader>
        <Input
          aria-label="Search groups"
          placeholder="Search groups"
          value={query}
          disabled={mutation.isPending}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div
          className="max-h-[45vh] space-y-2 overflow-y-auto"
          role="group"
          aria-label="Sticker groups"
        >
          {choices.map((pack) => (
            <label
              key={pack.packId}
              className="flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm"
            >
              <input
                type="checkbox"
                aria-label={pack.displayName}
                checked={selected.includes(pack.packId)}
                disabled={mutation.isPending}
                onChange={() => toggle(pack.packId)}
              />
              <span className="flex-1">{pack.displayName}</span>
              {pack.status === 'DELISTED' && (
                <span className="text-xs text-muted-foreground">Hidden</span>
              )}
            </label>
          ))}
          {!choices.length && <p className="text-sm text-muted-foreground">No groups match.</p>}
        </div>
        <label className="space-y-1 text-sm">
          Primary group
          <select
            aria-label="Primary group"
            value={primary}
            disabled={mutation.isPending || !selected.length}
            onChange={(event) => setPrimary(event.target.value)}
            className="h-9 w-full rounded-md border bg-background px-2"
          >
            {!selected.length && <option value="">Choose a group first</option>}
            {selectedGroups.map((pack) => (
              <option key={pack.packId} value={pack.packId}>
                {pack.displayName}
              </option>
            ))}
          </select>
        </label>
        <p className="text-xs text-muted-foreground">
          The primary group identifies this sticker in its details. Hidden groups stay hidden in the
          mobile picker until relisted.
        </p>
        {!selected.length && <p className="text-sm text-destructive">Choose at least one group.</p>}
        {selected.length > selectedGroups.length && (
          <p role="alert" className="text-sm text-destructive">
            A selected group is no longer available. Close and reload the library.
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" disabled={mutation.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={mutation.isPending || !valid || !changed} onClick={() => void save()}>
            {mutation.isPending ? 'Saving…' : 'Save groups'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
